// Task #2569 — Config Socket.IO condivisa (unica fonte di verita`).
//
// Consolida la configurazione di transport/riconnessione usata dal client
// Socket.IO del tenant (SocketContext) e dalle copie ad-hoc del master
// (NetworkMonitoring, useClusterRealtime, QueueMonitor, NotificationBell).
// Robustezza a browser attivo: riconnessione infinita con jitter, mai resa
// permanente dopo N tentativi.
//
// Nota tipi: non importiamo `socket.io-client` qui (ui-frontend non lo ha tra
// le dipendenze). Esponiamo un oggetto fortemente tipizzato che e` un
// sotto-insieme delle opzioni accettate da `io(url, opts)`; i consumer lo
// fanno spread dentro la chiamata `io(...)`.

export type SocketTransport = 'polling' | 'websocket';

export interface SharedSocketConfig {
  readonly path: string;
  // Array mutabile: `io(url, opts)` di socket.io-client tipizza `transports`
  // come `string[]` mutabile, un `readonly` qui non sarebbe assegnabile.
  readonly transports: SocketTransport[];
  readonly upgrade: boolean;
  readonly reconnection: boolean;
  readonly reconnectionAttempts: number;
  readonly reconnectionDelay: number;
  readonly reconnectionDelayMax: number;
  /** Jitter de-correlato nativo di Socket.IO sul delay di riconnessione. */
  readonly randomizationFactor: number;
  readonly timeout: number;
  readonly withCredentials: boolean;
}

/**
 * URL del backend Socket.IO.
 * In produzione il traffico Socket.IO passa da un sottodominio `ws.` dedicato
 * (nginx :443 → backend LAN). La porta :8443 e` riservata a Kamailio
 * mod_websocket (WSS SIP), NON a Socket.IO. In dev usiamo l'origin corrente
 * (proxato da Vite).
 *
 * Task #3257 — mappa estendibile dominio-base → origin WS per prodotto:
 * nuovi domini prodotto vanno aggiunti qui; default = same-origin.
 */
const SOCKET_WS_ORIGIN_BY_BASE_DOMAIN: Readonly<Record<string, string>> = {
  'easyflowai.it': 'https://ws.easyflowai.it',
};

export function getSocketUrl(): string {
  if (typeof window === 'undefined') return '';
  const hostname = window.location.hostname.toLowerCase();
  for (const [baseDomain, wsOrigin] of Object.entries(SOCKET_WS_ORIGIN_BY_BASE_DOMAIN)) {
    if (hostname === baseDomain || hostname.endsWith(`.${baseDomain}`)) {
      return wsOrigin;
    }
  }
  return window.location.origin;
}

/**
 * Config condivisa Socket.IO. Riconnessione infinita con jitter: il client non
 * si arrende mai per cause transitorie (sleep/wake, cambio rete, NAT rebind).
 */
export function getSocketConfig(): SharedSocketConfig {
  return {
    path: '/socket.io/',
    transports: ['websocket', 'polling'],
    upgrade: true,
    reconnection: true,
    reconnectionAttempts: Number.POSITIVE_INFINITY,
    reconnectionDelay: 1000,
    reconnectionDelayMax: 30000,
    randomizationFactor: 0.5,
    timeout: 20000,
    withCredentials: true,
  };
}
